export interface MonthlyCollectionRow {
  program_id: string;
  program_name: string;
  total_invoiced: number;
  amount_paid: number;
  amount_processing: number;
  amount_outstanding: number;
  invoice_count: number;
  invoices_paid: number;
  invoices_partial: number;
  invoices_pending: number;
  invoices_overdue: number;
  pct_collected: number;
  pct_processing: number;
  invoices_covered: number;
}

export interface CollectionSummary {
  total_invoiced: number;
  total_paid: number;
  total_processing: number;
  total_outstanding: number;
  pct_collected: number;
  pct_processing: number;
}

/** Aggregate per-program rows into a single summary for the header KPI row. */
export function summarizeCollection(rows: MonthlyCollectionRow[]): CollectionSummary {
  if (rows.length === 0) {
    return {
      total_invoiced: 0,
      total_paid: 0,
      total_processing: 0,
      total_outstanding: 0,
      pct_collected: 0,
      pct_processing: 0,
    };
  }
  const total_invoiced    = rows.reduce((s, r) => s + r.total_invoiced, 0);
  const total_paid        = rows.reduce((s, r) => s + r.amount_paid, 0);
  const total_processing  = rows.reduce((s, r) => s + r.amount_processing, 0);
  const total_outstanding = total_invoiced - total_paid - total_processing;
  return {
    total_invoiced,
    total_paid,
    total_processing,
    total_outstanding,
    pct_collected:  total_invoiced > 0 ? Math.round((total_paid / total_invoiced) * 1000) / 10 : 0,
    pct_processing: total_invoiced > 0 ? Math.round((total_processing / total_invoiced) * 1000) / 10 : 0,
  };
}

/** Return a traffic-light status for a program's collection progress. */
export function collectionStatus(row: MonthlyCollectionRow): 'on-track' | 'partial' | 'at-risk' {
  if (row.pct_collected >= 90) return 'on-track';
  if (row.pct_collected >= 50 || row.invoices_overdue === 0) return 'partial';
  return 'at-risk';
}

// ─── Enrollment Progress ─────────────────────────────────────────────────────

export type LeadTimeProfile = 'sprint' | 'medium' | 'marathon';

export interface EnrollmentCurvePoint {
  day: number;
  count: number;
}

export interface EnrollmentProgressRow {
  program_id: string;
  program_name: string;
  billing_type: string;
  program_type: string;
  start_date: string;
  lead_time_days: number;
  lead_time_profile: LeadTimeProfile;
  days_until_start: number;
  t0_day: number;
  current_enrollment: number;
  first_7_day_enrollment: number;
  week1_capture_pct: number;
  median_enroll_day: number;
  expected_pct_at_t0: number;
  projected_final_count: number;
  prior_term_enrollment: number;
  prior_term_at_same_t0: number;
  pct_vs_prior_at_same_t0: number | null;
  pct_vs_prior_final: number | null;
  enrollment_curve: EnrollmentCurvePoint[];
  prior_term_curve: EnrollmentCurvePoint[];
}

export interface EnrollmentVelocityDetailRow {
  enroll_day: number;
  enrolled_at: string;
  student_name: string;
  parent_name: string;
  is_bhcs_member: boolean;
}

/**
 * Classify lead time in days into a profile label.
 * Mirrors the CASE expression in get_insight_enrollment_progress().
 */
export function classifyLeadTime(leadTimeDays: number): LeadTimeProfile {
  if (leadTimeDays < 14) return 'sprint';
  if (leadTimeDays <= 35) return 'medium';
  return 'marathon';
}

/**
 * Compute projected final enrollment count.
 * Divides current count by the expected fraction at the current T0 day,
 * clamped so it is never less than current enrollment.
 */
export function computeProjectedCount(currentEnrollment: number, expectedPctAtT0: number): number {
  if (expectedPctAtT0 <= 0) return currentEnrollment;
  return Math.max(currentEnrollment, Math.round(currentEnrollment / expectedPctAtT0));
}

/**
 * Return whether a Marathon program is in its historically active late-surge window
 * (7–14 days before start). This is when reminder campaigns drive 30–50% of final enrollment.
 */
export function isInLateSurgeWindow(row: Pick<EnrollmentProgressRow, 'lead_time_profile' | 'days_until_start'>): boolean {
  return row.lead_time_profile === 'marathon' &&
    row.days_until_start >= 7 &&
    row.days_until_start <= 14;
}

/**
 * Return whether a Sprint program is approaching its deadline with lagging enrollment.
 * Fires when <= 3 days remain and current count is below 85% of projection.
 */
export function isSprintLagging(row: Pick<EnrollmentProgressRow, 'lead_time_profile' | 'days_until_start' | 'current_enrollment' | 'projected_final_count'>): boolean {
  return row.lead_time_profile === 'sprint' &&
    row.days_until_start <= 3 &&
    row.days_until_start > 0 &&
    row.projected_final_count > 0 &&
    row.current_enrollment < row.projected_final_count * 0.85;
}

// ─── Enrollment Funnel ───────────────────────────────────────────────────────

export interface FunnelStepRow {
  step: string;
  step_order: number;
  unique_users: number;
  drop_off_pct: number | null;
  conversion_pct: number | null;
}

// ─── Family Personas ─────────────────────────────────────────────────────────
// One row per household -- every active parent linked through a shared student
// (get_insight_family_households). The segment is engagement only; payment
// behaviour (autopay, past due, method) is reported beside it.

export type FamilySegment =
  | 'Power Family'
  | 'Afterschool + Class'
  | 'Afterschool Only'
  | 'Classes & Camps'
  | 'Lapsed'
  | 'Not Yet Enrolled';

/** Display order, most engaged first. */
export const FAMILY_SEGMENTS: FamilySegment[] = [
  'Power Family',
  'Afterschool + Class',
  'Afterschool Only',
  'Classes & Camps',
  'Lapsed',
  'Not Yet Enrolled',
];

const ENROLLED_SEGMENTS: ReadonlySet<FamilySegment> = new Set<FamilySegment>([
  'Power Family',
  'Afterschool + Class',
  'Afterschool Only',
  'Classes & Camps',
]);

/** True for segments with an active class, camp, or afterschool enrollment. */
export function isEnrolledSegment(segment: FamilySegment): boolean {
  return ENROLLED_SEGMENTS.has(segment);
}

export interface HouseholdRow {
  household_id: string;
  segment: FamilySegment;
  guardians: string;
  emails: string[] | null;
  students: string | null;
  parent_accounts: number;
  active_students: number;
  afterschool: boolean;
  class_enrollments: number;
  camp_enrollments: number;
  billed_this_year: number;
  autopay: boolean;
  past_due_amount: number;
  dominant_method: string | null;
  new_this_year: boolean;
  here_last_year: boolean;
  afterschool_last_year: boolean;
  last_program: string | null;
  last_end_date: string | null;
}

export interface SegmentSummary {
  segment: FamilySegment;
  households: number;
  parentAccounts: number;
  avgStudents: number;
  avgBilledThisYear: number;
  pctAutopay: number;
  pastDue: number;
  newThisYear: number;
  afterschoolLastYear: number;
  dominantMethod: string | null;
}

export interface FamilyOverview {
  households: number;
  parentAccounts: number;
  enrolled: number;
  /** Enrolled households whose first class, camp, or afterschool started this school year. */
  newThisYear: number;
  autopayEnrolled: number;
  /** Households in afterschool last school year, and how many are in afterschool now. */
  afterschoolLastYear: number;
  afterschoolRenewed: number;
  pastDueHouseholds: number;
  pastDueAmount: number;
}

function pct(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

function mostCommon(values: Array<string | null>): string | null {
  const counts = new Map<string, number>();
  for (const v of values) if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
  let best: string | null = null;
  for (const [v, n] of [...counts].sort(([a], [b]) => a.localeCompare(b))) {
    if (best === null || n > (counts.get(best) ?? 0)) best = v;
  }
  return best;
}

/** Per-segment cards (every segment, in display order) and the panel's headline numbers. */
export function summarizeHouseholds(rows: HouseholdRow[]): {
  overview: FamilyOverview;
  segments: SegmentSummary[];
} {
  const segments = FAMILY_SEGMENTS.map((segment): SegmentSummary => {
    const group = rows.filter(r => r.segment === segment);
    const n = group.length;
    const sum = (f: (r: HouseholdRow) => number) => group.reduce((acc, r) => acc + f(r), 0);
    return {
      segment,
      households: n,
      parentAccounts: sum(r => Number(r.parent_accounts)),
      avgStudents: n > 0 ? Math.round((sum(r => Number(r.active_students)) / n) * 10) / 10 : 0,
      avgBilledThisYear: n > 0 ? Math.round((sum(r => Number(r.billed_this_year)) / n) * 100) / 100 : 0,
      pctAutopay: pct(group.filter(r => r.autopay).length, n),
      pastDue: group.filter(r => Number(r.past_due_amount) > 0).length,
      newThisYear: group.filter(r => r.new_this_year).length,
      afterschoolLastYear: group.filter(r => r.afterschool_last_year).length,
      dominantMethod: mostCommon(group.map(r => r.dominant_method)),
    };
  });

  const enrolled = rows.filter(r => isEnrolledSegment(r.segment));
  const afterschoolLastYear = rows.filter(r => r.afterschool_last_year);
  const pastDue = rows.filter(r => Number(r.past_due_amount) > 0);

  return {
    overview: {
      households: rows.length,
      parentAccounts: rows.reduce((acc, r) => acc + Number(r.parent_accounts), 0),
      enrolled: enrolled.length,
      newThisYear: enrolled.filter(r => r.new_this_year).length,
      autopayEnrolled: enrolled.filter(r => r.autopay).length,
      afterschoolLastYear: afterschoolLastYear.length,
      afterschoolRenewed: afterschoolLastYear.filter(r => r.afterschool).length,
      pastDueHouseholds: pastDue.length,
      pastDueAmount: Math.round(pastDue.reduce((acc, r) => acc + Number(r.past_due_amount), 0) * 100) / 100,
    },
    segments,
  };
}

/**
 * The order a segment's family list reads in: enrolled families by what they were billed this
 * school year, lapsed families with last year's afterschool families first and then the most
 * recently ended, families not yet enrolled by name.
 */
export function sortHouseholds(rows: HouseholdRow[], segment: FamilySegment): HouseholdRow[] {
  const byName = (a: HouseholdRow, b: HouseholdRow) => a.guardians.localeCompare(b.guardians);
  if (segment === 'Lapsed') {
    return [...rows].sort((a, b) =>
      Number(b.afterschool_last_year) - Number(a.afterschool_last_year)
      || (b.last_end_date ?? '').localeCompare(a.last_end_date ?? '')
      || byName(a, b));
  }
  if (segment === 'Not Yet Enrolled') return [...rows].sort(byName);
  return [...rows].sort((a, b) => Number(b.billed_this_year) - Number(a.billed_this_year) || byName(a, b));
}

/** Color and wording for each segment card. */
export const SEGMENT_META: Record<FamilySegment, { color: string; description: string; hint: string }> = {
  'Power Family':        { color: 'text-purple-700 bg-purple-50 border-purple-200', description: 'Afterschool plus 2+ class enrollments', hint: 'Your most engaged families' },
  'Afterschool + Class': { color: 'text-blue-700 bg-blue-50 border-blue-200',       description: 'Afterschool plus one class',            hint: 'One class away from Power Family' },
  'Afterschool Only':    { color: 'text-teal-700 bg-teal-50 border-teal-200',       description: 'Afterschool, no classes',               hint: 'Audience for class promotions' },
  'Classes & Camps':     { color: 'text-amber-700 bg-amber-50 border-amber-200',    description: 'Classes or camps, no afterschool',      hint: 'Afterschool prospects' },
  'Lapsed':              { color: 'text-rose-700 bg-rose-50 border-rose-200',       description: 'Enrolled before, nothing active now',   hint: 'Win-back list' },
  'Not Yet Enrolled':    { color: 'text-gray-600 bg-gray-50 border-gray-200',       description: 'No class, camp, or afterschool yet',    hint: 'Accounts that never enrolled' },
};
